import { Matrix3, Matrix4, Vector3 } from "three";

import SceneObjectData from "./SceneObjectData";
import SceneObjectTypeEnum from "./SceneObjectTypeEnum";
import { vector3ToJson, vector3FromJson } from "../../services/bimVisualizerApi/primitivesSerialization";
import { UUID } from "../../utils/typeDefinitions";
import Constants from "../../constants";
import { mmFromSceneUnits, sceneUnitsFromMm } from "../../utils/scaler";
import apiLengthDimensionDataSchema from "../../services/bimVisualizerApi/schema/document/sceneObject/lengthDimensionDataSchema";

class LengthDimensionData extends SceneObjectData {
    start: Vector3;
    end: Vector3;
    offset: Vector3;
    text: string;
    fontSize: number;

    constructor(    start: Vector3, end: Vector3,
                    offset: Vector3 = new Vector3(0, 0, 0),
                    text: string = "",
                    fontSize: number = 0.07,
                    uuid?: UUID,
                    parentId?: UUID) {
        super(SceneObjectTypeEnum.LengthDimension, uuid, parentId);
        this.start = start;
        this.end = end;
        this.offset = offset;
        this.text = text;
        this.fontSize = fontSize;
    }

    clone() : this {
        const clone = new LengthDimensionData( this.start.clone(), this.end.clone(),
                                        this.offset.clone(), this.text, this.fontSize,
                                        this.uuid, this.parentId) as this;

        this.copyBaseData(clone);
        return clone;
    }

    applyMatrix4(matrix: Matrix4): void {
        this.start.applyMatrix4(matrix);
        this.end.applyMatrix4(matrix);
        
        this.offset.applyMatrix3(new Matrix3().setFromMatrix4(matrix));
    }

    computeDisplayText(): string {
        const lengthInCm = mmFromSceneUnits(this.start.distanceTo(this.end)) / 10;
        return `${Math.round(lengthInCm)}cm`;
    }

    toJson(): any {
        return {
            ...super.toJson(),
            start: vector3ToJson(this.start, Constants.IO_LOCATION_PRECISION, mmFromSceneUnits),
            end: vector3ToJson(this.end, Constants.IO_LOCATION_PRECISION, mmFromSceneUnits),
            offset: vector3ToJson(this.offset, Constants.IO_LOCATION_PRECISION, mmFromSceneUnits),
            text: this.text,
            fontSize: mmFromSceneUnits(this.fontSize)
        };
    }

    static fromJson(json: any): LengthDimensionData {

        const parsedData = apiLengthDimensionDataSchema.parse(json);

        var data = new LengthDimensionData(
            vector3FromJson(parsedData.start, sceneUnitsFromMm),
            vector3FromJson(parsedData.end, sceneUnitsFromMm),
            vector3FromJson(parsedData.offset, sceneUnitsFromMm),
            parsedData.text,
            sceneUnitsFromMm(parsedData.fontSize ?? 70),
            parsedData.uuid,
            parsedData.parentId ?? undefined
        );
        
        return data;
    }
}

export default LengthDimensionData;